/**
 * Scrub sensitive fields before Sentry events leave the browser.
 * Never log raw secret values — only redact in place.
 * Also redact assessment answers / score payloads (NO_STUDENT_LEVEL_DATA_TO_AIDEON).
 */
import { isPiiField } from './privacy/piiFields';
import { isAssessmentTelemetryBlockedKey } from './privacy/assessmentTelemetryGuard';

const REDACTED = '[Filtered]';

/** Keys (case-insensitive) whose values must not reach Sentry. */
const SENSITIVE_KEY_RE =
  /^(password|passwd|passcode|secret|token|session_token|api_?key|authorization|cookie|access_token|refresh_token|id_token|jwt|bearer|dsn|private_?key|service_?role)$/i;

/** Substrings in key names that indicate sensitive data. */
const SENSITIVE_KEY_SUBSTRINGS = [
  'password',
  'passcode',
  'secret',
  'token',
  'authorization',
  'apikey',
  'api_key',
  'session_token',
  'service_role',
];

function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase();
  if (SENSITIVE_KEY_RE.test(lower)) return true;
  return SENSITIVE_KEY_SUBSTRINGS.some((s) => lower.includes(s));
}

/**
 * Identity/PII-looking keys (playerName, display_name, email, ...) — see
 * `src/lib/privacy/piiFields.ts`. Separate from `isSensitiveKey` (secrets)
 * because the two have different purposes, but both are redacted the same
 * way before an event leaves the browser. A student's nimimerkki (game
 * display name) must never reach Sentry — see docs/PRIVACY_ANALYTICS.md.
 */
function isPiiKey(key: string): boolean {
  return isPiiField(key);
}

function scrubValue(value: unknown, depth = 0): unknown {
  if (depth > 8) return REDACTED;
  if (value == null || typeof value === 'boolean' || typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    // JWT-shaped bearer tokens
    if (/^eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\./.test(value)) {
      return REDACTED;
    }
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => scrubValue(item, depth + 1));
  }
  if (typeof value === 'object') {
    return scrubObject(value as Record<string, unknown>, depth + 1);
  }
  return value;
}

function scrubObject(obj: Record<string, unknown>, depth = 0): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(obj)) {
    out[key] =
      isSensitiveKey(key) || isPiiKey(key) || isAssessmentTelemetryBlockedKey(key)
        ? REDACTED
        : scrubValue(val, depth);
  }
  return out;
}

/** Scrub request headers on a Sentry event (mutates in place). */
export function scrubSentryEvent<T extends Record<string, unknown>>(event: T): T {
  const request = event.request as Record<string, unknown> | undefined;
  if (request?.headers && typeof request.headers === 'object') {
    request.headers = scrubObject(request.headers as Record<string, unknown>);
  }

  if (event.extra && typeof event.extra === 'object') {
    event.extra = scrubObject(event.extra as Record<string, unknown>);
  }

  if (event.contexts && typeof event.contexts === 'object') {
    event.contexts = scrubObject(event.contexts as Record<string, unknown>);
  }

  // Defense-in-depth: no code path currently calls Sentry.setUser(), but
  // scrub it too in case that changes (e.g. username/email/playerName).
  if (event.user && typeof event.user === 'object') {
    event.user = scrubObject(event.user as Record<string, unknown>);
  }

  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map((crumb: Record<string, unknown>) => {
      if (crumb.data && typeof crumb.data === 'object') {
        return { ...crumb, data: scrubObject(crumb.data as Record<string, unknown>) };
      }
      return crumb;
    });
  }

  return event;
}

/** Scrub breadcrumb data payloads. */
export function scrubBreadcrumbData(
  data: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!data || typeof data !== 'object') return data;
  return scrubObject(data);
}

export { REDACTED, isSensitiveKey, isPiiKey };
